import { z } from 'zod';

/**
 * Opinie uczestników (PRD §10 pkt 46, PRD 16 US-708, US-709): zalogowany użytkownik wysyła opinię z dowolnego ekranu
 * (PRD §10 pkt 53; edycja z adresu strony, jeśli w nim jest), admin przegląda je i oznacza jako obejrzane albo załatwione. Czasy jako ISO 8601.
 */

/** Kolejność ma znaczenie: tak pokazujemy rodzaje w formularzu. */
export const feedbackKinds = ['positive', 'problem', 'improvement'] as const;
export const feedbackKindSchema = z.enum(feedbackKinds);
export type FeedbackKind = z.infer<typeof feedbackKindSchema>;

export const feedbackStatuses = ['new', 'seen', 'resolved'] as const;
export const feedbackStatusSchema = z.enum(feedbackStatuses);
export type FeedbackStatus = z.infer<typeof feedbackStatusSchema>;

export const FEEDBACK_BODY_MIN = 3;
export const FEEDBACK_BODY_MAX = 2000;

export const feedbackInputSchema = z.object({
  kind: feedbackKindSchema,
  body: z.string().trim().min(FEEDBACK_BODY_MIN).max(FEEDBACK_BODY_MAX),
  /** Adres strony, z której wysłano (np. szczegóły zajęć) – pomaga zrozumieć zgłoszenie. */
  pagePath: z
    .string()
    .max(300)
    .regex(/^\/[^\s]*$/)
    .optional(),
  /** Edycja z adresu strony (np. `/edycje/:slug/...`); nieznana albo brak – opinia bez edycji. */
  editionSlug: z.string().trim().min(1).max(100).optional(),
});
export type FeedbackInput = z.infer<typeof feedbackInputSchema>;

/** Wartość filtra „bez edycji” w panelu admina. */
export const FEEDBACK_NO_EDITION = 'none';

const emptyToUndefined = (value: unknown) => (value === '' ? undefined : value);

/** `open` = nowe i obejrzane (domyślnie – to, czym trzeba się jeszcze zająć); `all` – wszystkie. */
export const feedbackStatusFilters = ['open', 'all', ...feedbackStatuses] as const;
export type FeedbackStatusFilter = (typeof feedbackStatusFilters)[number];

export const feedbackQuerySchema = z.object({
  kind: z.preprocess(emptyToUndefined, feedbackKindSchema.optional()),
  status: z.preprocess(emptyToUndefined, z.enum(feedbackStatusFilters).default('open')),
  /** `none` – opinie wysłane spoza ekranów edycji. */
  editionId: z.preprocess(emptyToUndefined, z.union([z.literal(FEEDBACK_NO_EDITION), z.string().uuid()]).optional()),
  q: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
  page: z.preprocess(emptyToUndefined, z.coerce.number().int().min(1).default(1)),
});
export type FeedbackQuery = z.infer<typeof feedbackQuerySchema>;

export const feedbackStatusChangeSchema = z.object({
  from: feedbackStatusSchema,
  to: feedbackStatusSchema,
  by: z.string().nullable(),
  at: z.string(),
});
export type FeedbackStatusChange = z.infer<typeof feedbackStatusChangeSchema>;

export const feedbackEntrySchema = z.object({
  id: z.string().uuid(),
  kind: feedbackKindSchema,
  body: z.string(),
  status: feedbackStatusSchema,
  adminNote: z.string().nullable(),
  pagePath: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  /** `null` – konto usunięte. */
  author: z.object({ accountId: z.string().uuid(), name: z.string(), email: z.string() }).nullable(),
  edition: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  history: z.array(feedbackStatusChangeSchema),
});
export type FeedbackEntry = z.infer<typeof feedbackEntrySchema>;

export const feedbackPageSchema = z.object({
  entries: z.array(feedbackEntrySchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type FeedbackPage = z.infer<typeof feedbackPageSchema>;

export const feedbackUpdateSchema = z
  .object({
    status: feedbackStatusSchema.optional(),
    adminNote: z.string().trim().max(FEEDBACK_BODY_MAX).optional(),
  })
  .refine((v) => v.status !== undefined || v.adminNote !== undefined, { message: 'Podaj status albo notatkę.' });
export type FeedbackUpdate = z.infer<typeof feedbackUpdateSchema>;

export const feedbackBulkStatusSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
  status: feedbackStatusSchema,
});
export type FeedbackBulkStatus = z.infer<typeof feedbackBulkStatusSchema>;

/** Licznik w menu admina. */
export const feedbackSummarySchema = z.object({ new: z.number().int() });
export type FeedbackSummary = z.infer<typeof feedbackSummarySchema>;
